import { useState, useReducer, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import { cloneDeep } from 'lodash';

// Hook
import useMessage from "hooks/useMessage";
import usePageContext from "hooks/usePageContext";

// Slice
import { uiActions, selectShowModalSpinner } from "stores/slice/ui";

// UI
import SpinnerForModal from "ui/SpinnerForModal";

// Component
import MessageBoxGroup from 'components/MessageBoxGroup';
import Input from 'components/Input';
import Button from 'components/Button';
import ModalContainer from 'components/ModalContainer';
import DropdownWithItem from 'components/DropdownWithItem';
import DropdownWithCheckbox from 'components/DropdownWithCheckbox';

// Model
import SyslogServerModel from '../models/SyslogServer';

// Store
import syslogServerStore from '../stores/syslogServer';

// Const
import { syslogServerField } from "../const";

// Controller
import controller from "../controller";

const AddOrEditSyslogServerModal = props => {
  const {
    modalStatus,
    changeModalStatus,
    selectedSyslogServer = null,
    managedSiteList,
    remainingSiteList,
    successCallback
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Slice
  const showModalSpinner = useSelector(selectShowModalSpinner);

  // Hook
  const { selectedOrg } = usePageContext();
  const {
    messages,
    messagesType,
    resetMessages,
    setErrorMessage
  } = useMessage();

  // State
  const [syslogServerState, syslogServerDispatch] = useReducer(
    syslogServerStore.reducer,
    syslogServerStore.defaultState
  );

  // Method
  const init = useCallback(() => {
    syslogServerDispatch({
      type: syslogServerStore.action.initializeState,
      payload: SyslogServerModel.getSyslogServerState(selectedSyslogServer, remainingSiteList, managedSiteList)
    });
  }, [selectedSyslogServer, remainingSiteList]);

  const changeField = (field, value) => {
    const getMethodName = (str) => {
      const firstLetter = str.charAt(0);
      const firstLetterCap = firstLetter.toUpperCase();
      const remainingLetters = str.slice(1);

      return firstLetterCap + remainingLetters;
    }

    const updatedField = cloneDeep(syslogServerState[field]);
    const methodName = `change${getMethodName(field)}`;
    SyslogServerModel[methodName](updatedField, value, syslogServerState);

    syslogServerDispatch({
      type: syslogServerStore.action[methodName],
      payload: updatedField
    });
  }

  const changeSite = (item) => {
    const updatedSite = cloneDeep(syslogServerState.site);
    SyslogServerModel.changeSite(updatedSite, item);

    syslogServerDispatch({
      type: syslogServerStore.action.changeSite,
      payload: updatedSite
    });
  }

  const checkForm = () => {
    let state = true;
    for (const fieldKey in syslogServerState) {
      if (fieldKey === syslogServerField.protocol || fieldKey === syslogServerField.site) {
        continue;
      }

      const { valid } = syslogServerState[fieldKey];
      if (!valid) {
        state = false;
      }
    }

    return state;
  }

  const addOrSave = async () => {
    try {
      const selectedSyslogServerId = selectedSyslogServer?.rawData.id || null;
      const response = await controller.addOrSaveSyslogServer(selectedOrg.id, selectedSyslogServerId, syslogServerState);

      if (response.error) {
        throw response.error;
      }

      dispatch(uiActions.hideModalSpinner());
      resetMessages();
      changeModalStatus(modalStatus.addOrEdit.type, false);
      successCallback();
    } catch (error) {
      setErrorMessage(error.code);
      dispatch(uiActions.hideModalSpinner());
    }
  }

  // Side effect
  useEffect(() => {
    if (modalStatus.addOrEdit.status) {
      resetMessages();
      dispatch(uiActions.showModalSpinner());
      init();
      setTimeout(() => {
        dispatch(uiActions.hideModalSpinner());
      }, 1000);
    }
  }, [dispatch, init, modalStatus.addOrEdit.status]);

  return (
    <ModalContainer
      modalWidthType="modal-500px"
      openModal={modalStatus.addOrEdit.status}
      closeModal={() => changeModalStatus(modalStatus.addOrEdit.type, false)}
    >
      {showModalSpinner && <SpinnerForModal />}
      <div className="header">
        <div className="title">
          {!selectedSyslogServer && t('7181448e22')}
          {selectedSyslogServer && t('ef1f73fcc0')}
        </div>
      </div>
      <div className="body">
        <MessageBoxGroup
          messages={messages}
          ErrorMessagesType={messagesType.error.server}
          onClose={resetMessages}
        />
        <Row className="mb-2">
          <Col sm={6}>
            {/* Name */}
            <div className="modal-form-title required">{t('49ee308734')}</div>
            <Input
              type="text"
              placeholder="7563cde047"
              isInvalid={syslogServerState.name.dirty && !syslogServerState.name.valid}
              value={syslogServerState.name.value}
              onChange={e => changeField(syslogServerField.name, e.target.value)}
            />
          </Col>
          <Col sm={6}>
            {/* Syslog server */}
            <div className="modal-form-title required">{t('2611bdb264')}</div>
            <Input
              type="text"
              placeholder="192.168.200.1 or FQDN"
              isInvalid={syslogServerState.server.dirty && !syslogServerState.server.valid}
              value={syslogServerState.server.value}
              onChange={e => changeField(syslogServerField.server, e.target.value)}
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col sm={6}>
            {/* Syslog server port */}
            <div className="modal-form-title required">{t('716f54f97a')}</div>
            <Input
              type="number"
              placeholder="1-65535"
              isInvalid={syslogServerState.port.dirty && !syslogServerState.port.valid}
              value={syslogServerState.port.value}
              onChange={e => changeField(syslogServerField.port, e.target.value)}
            />
          </Col>
          <Col sm={6}>
            {/* Protocol */}
            <div className="modal-form-title required">{t('888a77f5ac')}</div>
            <DropdownWithItem
              id="protocol-dropdown"
              type="normal"
              selectedItem={syslogServerState.protocol.selected}
              itemList={syslogServerState.protocol.list}
              onClick={protocol => changeField(syslogServerField.protocol, protocol)}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={6}>
            {/* Select site */}
            <div className="modal-form-title">{t('bd6e33b9d8')}</div>
            <DropdownWithCheckbox
              id="site-list-dropdown"
              label="c7d0b2bf4f"
              type="checkbox"
              itemList={syslogServerState.site.list}
              onChange={item => changeSite(item)}
            />
          </Col>
        </Row>
      </div>
      <div className="footer">
        <Button
          label="4a53b84974"
          className="btn-cancel"
          onClick={() => changeModalStatus(modalStatus.addOrEdit.type, false)}
        />
        <Button
          label="c9cc8cce24"
          className="btn-submit"
          onClick={addOrSave}
          disabled={!checkForm()}
        />
      </div>
    </ModalContainer>
  );
};

export default AddOrEditSyslogServerModal;
